/**
 * LeadMagnetBuilder Modal
 * 
 * Generates lead magnets based on user's business with automatic business type detection.
 * Output varies by plan tier:
 * - Starter: Text content + DIY instructions
 * - Growth: Editable template format  
 * - Pro: Finished, downloadable version
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MagicWand, Sparkle, Lightning, CheckCircle, Copy, Download, 
  ArrowRight, FileText, ListChecks, Article, Note, BookOpen,
  Spinner, CaretDown, Info, Eye, PencilSimple, ArrowUpRight, Lock, ArrowDown
} from '@phosphor-icons/react';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { isTrialUser, canDownload, getUpgradeMessage, getEffectiveTier } from '@/utils/trialAccess';
import { toast } from 'sonner';
import api from '@/utils/api';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';
import useBusinessMemoryPrefill from '@/hooks/useBusinessMemoryPrefill';
import useFoundationAutoStart from '@/hooks/useFoundationAutoStart';
import useFoundationAutoRoute from '@/hooks/useFoundationAutoRoute';
import { useMissionHandoff } from '@/store/missionHandoffContext';
import { normalizeBuilderAsset } from '@/utils/normalizeBuilderAsset';
import BuilderProgressOverlay from '@/components/BuilderProgressOverlay';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import NextStepGuide from './NextStepGuide';
import StyledHtmlPreview from './StyledHtmlPreview';
import RegenerateWithTweaks from './RegenerateWithTweaks';
import EditablePreview from './EditablePreview';

const LEAD_MAGNET_ICONS = {
  checklist: ListChecks,
  guide: BookOpen,
  template: FileText,
  swipe_file: Note,
  resource_list: Article
};

const TIER_BADGES = {
  starter: { label: 'Starter', color: 'bg-slate-600', description: 'Text + DIY Instructions' },
  growth: { label: 'Growth', color: 'bg-blue-600', description: 'Editable Template' },
  pro: { label: 'Pro', color: 'bg-purple-600', description: 'Done-For-You' }
};

export default function LeadMagnetBuilder({ isOpen, onClose, onAction, onPickStep, onSuccess, user }) {
  const [step, setStep] = useState('input'); // 'input', 'detecting', 'generating', 'result'
  const [businessDescription, setBusinessDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [detectedBusiness, setDetectedBusiness] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Round 108 — mission handoff prefill.
  const { handoff: missionHandoff, pickField: pickMissionField } = useMissionHandoff();
  const isMissionMode = !!(missionHandoff && missionHandoff.asset_id
    && (missionHandoff.asset_type === 'lead_magnet' || !missionHandoff.asset_type));

  useEffect(() => {
    if (!isOpen || !isMissionMode) return;
    const missionDesc = pickMissionField('title', 'subheadline', 'body', 'summary');
    if (missionDesc && typeof missionDesc === 'string') {
      setBusinessDescription(missionDesc);
    }
    const missionAudience = pickMissionField('recommended_audience', 'target_audience');
    if (missionAudience && typeof missionAudience === 'string') {
      setTargetAudience(missionAudience);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMissionMode]);

  // Determine effective plan tier (uses simulation-aware function)
  const effectiveTier = getEffectiveTier(user);
  const tierInfo = TIER_BADGES[effectiveTier] || TIER_BADGES.starter;
  
  // Check if user is on trial (simulation-aware)
  const isTrial = isTrialUser(user);
  const canUserDownload = canDownload(user);

  // Fetch available lead magnet types
  useEffect(() => {
    if (isOpen) {
      fetchTypes();
    }
  }, [isOpen]);

  // Canonical business memory prefill (hydrates from Business Builder, Business
  // Plan, Offers — user types only override explicitly)
  useBusinessMemoryPrefill(isOpen, {
    businessDescription: [businessDescription, setBusinessDescription],
    targetAudience: [targetAudience, setTargetAudience],
  });

  // Round 218A Phase 8D-3D — auto-consume Foundation when complete.
  useFoundationAutoStart({
    isOpen,
    ready: !!(businessDescription && targetAudience),
    step,
    onStart: () => handleGenerate(),
  });

  // Round 218A P0 — foundation gate.
  useFoundationAutoRoute({ isOpen, onClose });

  const fetchTypes = async () => {
    try {
      const response = await api.get('/lead-magnet/types');
      setAvailableTypes(response.data.types || []);
    } catch (error) {
      console.error('Failed to fetch types:', error);
    }
  };

  // Auto-detect business type as user types
  useEffect(() => {
    const detectTimeout = setTimeout(async () => {
      if (businessDescription.length >= 10) {
        try {
          const response = await api.post('/lead-magnet/detect-business', {
            business_description: businessDescription
          });
          setDetectedBusiness(response.data);
          if (!selectedType && response.data.recommended_lead_magnet) {
            setSelectedType(response.data.recommended_lead_magnet.type);
          }
        } catch (error) {
          // Silent fail for preview
        }
      }
    }, 500);

    return () => clearTimeout(detectTimeout);
  }, [businessDescription]);

  const handleGenerate = async () => {
    if (!businessDescription.trim()) {
      toast.error('Please describe your business');
      return;
    }

    setLoading(true);
    setStep('generating');

    try {
      const response = await api.post('/lead-magnet/generate', {
        business_description: businessDescription,
        target_audience: targetAudience || null,
        lead_magnet_type: selectedType
      });

      setResult(response.data);
      setStep('result');
      toast.success('Lead magnet created!');
      // Round 110 — normalize builder output before forwarding.
      try {
        if (onSuccess) onSuccess({ meta: normalizeBuilderAsset(response.data) });
      } catch (_) { /* noop */ }
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate lead magnet');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  // Regenerate with targeted tweaks — keeps modal open, replaces preview on success.
  const [regenerating, setRegenerating] = useState(false);
  const handleRegenerate = async (updatedFields) => {
    setRegenerating(true);
    try {
      const response = await api.post('/lead-magnet/generate', {
        business_description: updatedFields.business_description || businessDescription,
        target_audience: updatedFields.target_audience || targetAudience || null,
        lead_magnet_type: selectedType,
        headline_tone: updatedFields.headline_tone || '',
        tweak_notes: updatedFields.tweak_notes || '',
        title: updatedFields.title || '',
        cta: updatedFields.cta || '',
      });
      setResult(response.data);
      if (updatedFields.target_audience) setTargetAudience(updatedFields.target_audience);
      toast.success('Lead magnet updated with your tweaks!');
    } catch (error) {
      console.error('Regeneration failed:', error);
      toast.error(error.response?.data?.detail || "Couldn't regenerate — your current preview is preserved.");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = (text) => {
    // Block copy for trial users
    if (isTrial) {
      setUpgradeMessage('Upgrade to copy full content. Trial users get preview only.');
      setShowUpgradePrompt(true);
      return;
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadHTML = async () => {
    /* Phase G — V2 grants export to every subscriber */
    // Check trial access
    if (!canUserDownload) {
      setUpgradeMessage(getUpgradeMessage('download_html'));
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!result?.formatted_output?.html_version) return;

    const blob = new Blob([result.formatted_output.html_version], { type: 'text/html' });
    await safeDownloadBlob(blob, `${result.title?.replace(/[^a-z0-9]/gi, '_') || 'lead_magnet'}.html`);
    toast.success('Downloaded HTML!');
  };

  const handleDownloadPDF = async () => {
    /* Phase G — V2 grants export to every subscriber */
    // Check trial access
    if (!canUserDownload) {
      setUpgradeMessage(getUpgradeMessage('download_pdf'));
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!result?.id || !result?.has_pdf) {
      toast.error('PDF not available');
      return;
    }

    try {
      const response = await api.get(`/lead-magnet/${result.id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      await safeDownloadBlob(blob, `${result.title?.replace(/[^a-z0-9]/gi, '_') || 'lead_magnet'}.pdf`);
      toast.success('PDF Downloaded!');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleReset = () => {
    setStep('input');
    setBusinessDescription('');
    setTargetAudience('');
    setSelectedType(null);
    setDetectedBusiness(null);
    setResult(null);
  };

  // ──────────────────────────────────────────────────────────
  // Universal Editable Preview Mode (Phase 2) — Lead Magnet
  //
  // PATCH /api/lead-magnet/{id} — server-side whitelist only allows
  // title / subtitle / tagline / content / formatted_output.
  // business_memory is NEVER touched.
  // ──────────────────────────────────────────────────────────
  const [savingLeadMagnetEdits, setSavingLeadMagnetEdits] = useState(false);
  const editableLeadMagnet = useMemo(() => ({
    title: result?.title || '',
    subtitle: result?.subtitle || '',
    tagline: result?.tagline || '',
  }), [result]);

  const handleSaveLeadMagnetEdits = useCallback(async (next) => {
    if (!result?.id) {
      toast.error('Lead magnet ID missing — please regenerate.');
      return;
    }
    setSavingLeadMagnetEdits(true);
    try {
      const body = {
        title: next.title,
        subtitle: next.subtitle,
        tagline: next.tagline,
      };
      const { data } = await api.patch(`/lead-magnet/${result.id}`, body, { timeout: 30000 });
      setResult((prev) => (prev ? { ...prev, ...body, last_edited_at: data?.saved_at } : prev));
    } finally {
      setSavingLeadMagnetEdits(false);
    }
  }, [result?.id]);

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="lead-magnet-builder-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <MagicWand size={20} weight="fill" className="text-white" />
              </div>
              <div>
                <h2 className="text-white text-lg font-bold">Lead Magnet Builder</h2>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tierInfo.color} text-white`}>
                    {tierInfo.label}
                  </span>
                  <span className="text-slate-400 text-xs">{tierInfo.description}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-lead-magnet-modal"
            >
              <X size={20} className="text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <AnimatePresence mode="wait">
              {/* INPUT STEP */}
              {step === 'input' && (
                <motion.div
                  key="input"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-5"
                >
                  <BusinessMemoryBanner />
                  {/* Business Description */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      What does your business do?
                    </label>
                    <textarea
                      value={businessDescription}
                      onChange={(e) => setBusinessDescription(e.target.value)}
                      placeholder="e.g., I run a dog walking service in downtown Seattle..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 resize-none"
                      rows={3}
                      data-testid="business-description-input"
                    />
                    
                    {/* Auto-detected business type */}
                    {detectedBusiness && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 flex items-center space-x-2 text-sm"
                      >
                        <Sparkle size={14} className="text-amber-400" weight="fill" />
                        <span className="text-slate-400">Detected:</span>
                        <span className="text-amber-400 font-medium">{detectedBusiness.detected_name}</span>
                        {detectedBusiness.recommended_lead_magnet && (
                          <>
                            <span className="text-slate-500">•</span>
                            <span className="text-slate-400">Best format:</span>
                            <span className="text-blue-400">{detectedBusiness.recommended_lead_magnet.name}</span>
                          </>
                        )}
                      </motion.div>
                    )}
                  </div>

                  {/* Target Audience (Optional) */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Who are your ideal customers? <span className="text-slate-500">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      placeholder="e.g., Busy professionals who own dogs"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
                      data-testid="target-audience-input"
                    />
                  </div>

                  {/* Lead Magnet Type Selector */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Lead Magnet Type
                    </label>
                    <button
                      onClick={() => setShowTypeSelector(!showTypeSelector)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-left flex items-center justify-between hover:bg-white/10 transition-colors"
                      data-testid="type-selector-button"
                    >
                      {selectedType ? (
                        <div className="flex items-center space-x-3">
                          {(() => {
                            const IconComponent = LEAD_MAGNET_ICONS[selectedType] || FileText;
                            return <IconComponent size={18} className="text-amber-400" />;
                          })()}
                          <span className="text-white">
                            {availableTypes.find(t => t.id === selectedType)?.name || selectedType}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-500">Auto-select based on business</span>
                      )}
                      <CaretDown size={16} className={`text-slate-400 transition-transform ${showTypeSelector ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Type Dropdown */}
                    <AnimatePresence>
                      {showTypeSelector && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="mt-2 bg-slate-800 border border-white/10 rounded-xl overflow-hidden"
                        >
                          {availableTypes.map((type) => {
                            const IconComponent = LEAD_MAGNET_ICONS[type.id] || FileText;
                            return (
                              <button
                                key={type.id}
                                onClick={() => {
                                  setSelectedType(type.id);
                                  setShowTypeSelector(false);
                                }}
                                className={`w-full px-4 py-3 flex items-center space-x-3 hover:bg-white/10 transition-colors text-left ${selectedType === type.id ? 'bg-amber-500/20' : ''}`}
                              >
                                <IconComponent size={18} className={selectedType === type.id ? 'text-amber-400' : 'text-slate-400'} />
                                <div>
                                  <p className={`text-sm font-medium ${selectedType === type.id ? 'text-amber-400' : 'text-white'}`}>
                                    {type.name}
                                  </p>
                                  <p className="text-xs text-slate-500">{type.description}</p>
                                </div>
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Tier Preview */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Info size={16} className="text-blue-400" />
                      <span className="text-white text-sm font-medium">What you'll get ({tierInfo.label})</span>
                    </div>
                    {effectiveTier === 'starter' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Complete text content to copy</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Step-by-step DIY instructions</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Free tool recommendations (Canva, Google Docs)</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'growth' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Editable template with [customizable] sections</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Multiple headline variations</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Call-to-action options</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'pro' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Finished, professional lead magnet</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Ready to download (HTML)</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>No editing needed - use immediately</span>
                        </li>
                      </ul>
                    )}
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={handleGenerate}
                    disabled={!businessDescription.trim() || loading}
                    className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="generate-lead-magnet-btn"
                  >
                    <MagicWand size={20} weight="fill" />
                    <span>Generate My Lead Magnet</span>
                    <ArrowRight size={18} weight="bold" />
                  </button>
                </motion.div>
              )}

              {/* GENERATING STEP */}
              {step === 'generating' && (
                <BuilderProgressOverlay visible={true} title="Creating your lead magnet…" />
              )}

              {/* RESULT STEP */}
              {step === 'result' && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  {/* Success Header */}
                  <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle size={24} className="text-emerald-400" weight="fill" />
                      <div>
                        <h3 className="text-white font-bold">Lead Magnet Created!</h3>
                        <p className="text-emerald-400 text-sm">Saved to My Creations</p>
                      </div>
                    </div>
                  </div>

                  {/* Detected Info */}
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Sparkle size={14} className="text-amber-400" weight="fill" />
                      <span className="text-slate-400">Business:</span>
                      <span className="text-amber-400">{result.detected_business_name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {(() => {
                        const IconComponent = LEAD_MAGNET_ICONS[result.lead_magnet_type] || FileText;
                        return <IconComponent size={14} className="text-blue-400" />;
                      })()}
                      <span className="text-slate-400">Type:</span>
                      <span className="text-blue-400">{result.lead_magnet_type_name}</span>
                    </div>
                  </div>

                  {/* Title & Subtitle */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <h4 className="text-white text-lg font-bold mb-1">{result.title}</h4>
                    {result.subtitle && (
                      <p className="text-slate-400 text-sm">{result.subtitle}</p>
                    )}
                  </div>

                  {/* Universal Editable Preview Mode — fix the title /
                      subtitle / tagline before downloading. Business
                      Memory is NEVER touched. */}
                  {result.id && (
                    <EditablePreview
                      draftKey={`lead-magnet-draft-${result.id}`}
                      originalValue={editableLeadMagnet}
                      onSave={handleSaveLeadMagnetEdits}
                      busy={savingLeadMagnetEdits}
                      savedAt={result.last_edited_at || null}
                      previewSlot={({ value }) => (
                        <div className="space-y-2 text-sm text-slate-200">
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Title</p>
                            <p className="text-white font-semibold">{value.title || '—'}</p>
                          </div>
                          {value.subtitle && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">Subtitle</p>
                              <p>{value.subtitle}</p>
                            </div>
                          )}
                          {value.tagline && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">Tagline</p>
                              <p className="text-emerald-300">{value.tagline}</p>
                            </div>
                          )}
                        </div>
                      )}
                      editSlot={({ value, setValue }) => (
                        <div className="space-y-3">
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Title</span>
                            <input
                              type="text"
                              value={value.title || ''}
                              onChange={(e) => setValue({ ...value, title: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-400 focus:outline-none"
                              data-testid="lead-magnet-edit-title"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Subtitle</span>
                            <input
                              type="text"
                              value={value.subtitle || ''}
                              onChange={(e) => setValue({ ...value, subtitle: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-400 focus:outline-none"
                              data-testid="lead-magnet-edit-subtitle"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Tagline</span>
                            <input
                              type="text"
                              value={value.tagline || ''}
                              onChange={(e) => setValue({ ...value, tagline: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-400 focus:outline-none"
                              data-testid="lead-magnet-edit-tagline"
                            />
                          </label>
                        </div>
                      )}
                    />
                  )}

                  {/* Tier-Specific Output */}
                  {result.formatted_output && (
                    <div className="space-y-4">
                      {/* STARTER OUTPUT */}
                      {result.formatted_output.output_type === 'text_with_instructions' && (
                        <>
                          {/* Content to Copy */}
                          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-white font-medium flex items-center space-x-2">
                                <FileText size={16} className="text-amber-400" />
                                <span>Your Content</span>
                              </h5>
                              <button
                                onClick={() => handleCopy(result.formatted_output.content_to_copy || '')}
                                className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium rounded-lg flex items-center space-x-1 transition-colors"
                              >
                                <Copy size={12} />
                                <span>{copied ? 'Copied!' : 'Copy'}</span>
                              </button>
                            </div>
                            <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-48 overflow-y-auto">
                              {typeof result.formatted_output.content_to_copy === 'string' 
                                ? result.formatted_output.content_to_copy 
                                : JSON.stringify(result.formatted_output.content_to_copy, null, 2)}
                            </pre>
                          </div>

                          {/* DIY Instructions */}
                          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                            <h5 className="text-white font-medium mb-3 flex items-center space-x-2">
                              <ListChecks size={16} className="text-blue-400" />
                              <span>How to Build It (DIY)</span>
                            </h5>
                            <ol className="space-y-2">
                              {(result.formatted_output.diy_instructions || []).map((instruction, i) => (
                                <li key={i} className="flex items-start space-x-3 text-sm">
                                  <span className="w-5 h-5 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                                    {i + 1}
                                  </span>
                                  <span className="text-slate-300">{instruction}</span>
                                </li>
                              ))}
                            </ol>
                          </div>

                          {/* Free Tools */}
                          {result.formatted_output.free_tools?.length > 0 && (
                            <div className="flex items-center space-x-2 text-sm text-slate-400">
                              <Lightning size={14} className="text-amber-400" />
                              <span>Recommended tools:</span>
                              <span className="text-white">{result.formatted_output.free_tools.join(', ')}</span>
                            </div>
                          )}
                        </>
                      )}

                      {/* GROWTH OUTPUT */}
                      {result.formatted_output.output_type === 'editable_template' && (
                        <>
                          {/* Regenerate with Tweaks */}
                          <RegenerateWithTweaks
                            fields={{
                              business_description: businessDescription,
                              target_audience: targetAudience,
                              title: result?.title || result?.formatted_output?.title || '',
                              cta: result?.formatted_output?.call_to_action || '',
                            }}
                            editable={['title', 'cta', 'target_audience']}
                            loading={regenerating}
                            onRegenerate={handleRegenerate}
                            testIdPrefix="lm-tweaks-growth"
                          />

                          {/* Live Styled Preview (matches exported HTML/PDF) */}
                          {result.formatted_output.html_version && (
                            <StyledHtmlPreview
                              html={result.formatted_output.html_version}
                              title="Your Editable Template — Live Preview"
                              downloadName="LeadMagnet_Template.html"
                            />
                          )}

                          {/* Download HTML button */}
                          {result.formatted_output.html_version && (
                            <button
                              onClick={async () => {
                                const blob = new Blob([result.formatted_output.html_version], { type: 'text/html' });
                                await safeDownloadBlob(blob, 'LeadMagnet_Template.html');
                              }}
                              className="w-full px-4 py-2.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-sm font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors"
                              data-testid="download-html-template-btn"
                            >
                              <ArrowDown size={14} />
                              <span>Download Editable HTML</span>
                            </button>
                          )}
                          {result.formatted_output.editing_guide && (
                            <p className="text-slate-500 text-xs">
                              {result.formatted_output.editing_guide}
                            </p>
                          )}

                          {/* Template Content (text) */}
                          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-white font-medium flex items-center space-x-2">
                                <PencilSimple size={16} className="text-blue-400" />
                                <span>Template Text</span>
                              </h5>
                              <button
                                onClick={() => handleCopy(result.formatted_output.template_content || '')}
                                className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs font-medium rounded-lg flex items-center space-x-1 transition-colors"
                              >
                                <Copy size={12} />
                                <span>{copied ? 'Copied!' : 'Copy'}</span>
                              </button>
                            </div>
                            <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-48 overflow-y-auto">
                              {typeof result.formatted_output.template_content === 'string'
                                ? result.formatted_output.template_content
                                : JSON.stringify(result.formatted_output.template_content, null, 2)}
                            </pre>
                          </div>

                          {/* Variations */}
                          {result.formatted_output.headline_variations?.length > 0 && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <h5 className="text-white font-medium mb-3">Headline Options</h5>
                              <div className="space-y-2">
                                {result.formatted_output.headline_variations.map((headline, i) => (
                                  <div key={i} className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                                    <span className="text-slate-300 text-sm">{headline}</span>
                                    <button
                                      onClick={() => handleCopy(headline)}
                                      className="p-1 hover:bg-white/10 rounded transition-colors"
                                    >
                                      <Copy size={12} className="text-slate-400" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* PRO OUTPUT */}
                      {result.formatted_output.output_type === 'done_for_you' && (
                        <>
                          {/* Download Buttons - HIDDEN for trial users */}
                          {!isTrial && (
                            <div className="flex space-x-3">
                              {/* Primary: Download PDF */}
                              <button
                                onClick={handleDownloadPDF}
                                disabled={!result?.has_pdf}
                                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="download-pdf-btn"
                              >
                                <Download size={18} weight="bold" />
                                <span>Download PDF</span>
                              </button>
                              
                              {/* Secondary: Download HTML */}
                              <button
                                onClick={handleDownloadHTML}
                                className="py-3 px-4 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
                                data-testid="download-html-btn"
                              >
                                <FileText size={16} />
                                <span>HTML</span>
                              </button>
                            </div>
                          )}
                          
                          {/* Trial user sees upgrade message instead of downloads */}
                          {isTrial && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                              <p className="text-red-400 font-medium mb-2">Downloads Locked</p>
                              <p className="text-slate-400 text-sm mb-3">Upgrade to Pro to download PDF and HTML files.</p>
                              <Link 
                                to="/pricing" 
                                className="inline-block px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700"
                              >
                                Upgrade Now
                              </Link>
                            </div>
                          )}

                          {/* Live Styled Preview (matches exported HTML/PDF) */}
                          {result.formatted_output.html_version ? (
                            <>
                              <RegenerateWithTweaks
                                fields={{
                                  business_description: businessDescription,
                                  target_audience: targetAudience,
                                  title: result?.title || result?.formatted_output?.title || '',
                                  cta: result?.formatted_output?.call_to_action || '',
                                }}
                                editable={['title', 'cta', 'target_audience']}
                                loading={regenerating}
                                onRegenerate={handleRegenerate}
                                testIdPrefix="lm-tweaks-pro"
                              />
                              <StyledHtmlPreview
                                html={result.formatted_output.html_version}
                                title="Live Preview — Finished Lead Magnet"
                                downloadName="LeadMagnet.html"
                              />
                            </>
                          ) : (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="text-white font-medium flex items-center space-x-2">
                                  <Eye size={16} className="text-purple-400" />
                                  <span>Preview</span>
                                </h5>
                                <button
                                  onClick={() => handleCopy(result.formatted_output.plain_text || '')}
                                  className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 text-xs font-medium rounded-lg flex items-center space-x-1 transition-colors"
                                >
                                  <Copy size={12} />
                                  <span>Copy Text</span>
                                </button>
                              </div>
                              <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-48 overflow-y-auto">
                                {result.formatted_output.plain_text || ''}
                              </pre>
                            </div>
                          )}

                          {/* Usage Instructions */}
                          {result.formatted_output.usage_instructions && (
                            <div className="flex items-center space-x-2 text-sm text-slate-400 bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                              <CheckCircle size={16} className="text-purple-400" weight="fill" />
                              <span>{result.formatted_output.usage_instructions}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Upgrade Prompt for Non-Pro Users */}
                  {result?.delivery_mode !== 'done_for_you' && effectiveTier !== 'pro' && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4" data-testid="upgrade-prompt">
                      <p className="text-white text-sm font-medium mb-2">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Want this finished and ready to use?'
                          : 'Want this fully built and ready to download?'
                        }
                      </p>
                      <p className="text-slate-400 text-xs mb-3">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Upgrade to Pro to get done-for-you assets.'
                          : 'Upgrade to Pro.'
                        }
                      </p>
                      <Link
                        to="/pricing"
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all"
                        data-testid="upgrade-btn"
                      >
                        <span>Upgrade</span>
                        <ArrowUpRight size={16} weight="bold" />
                      </Link>
                    </div>
                  )}

                  {/* Action Buttons — Sequential workflow routing */}
                  <div className="space-y-2 pt-2">
                    <button
                      onClick={() => { onAction?.('email-sequence'); handleClose(); }}
                      className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
                      data-testid="next-email-sequence-btn"
                    >
                      <span>Next: Create Email Sequence</span>
                      <ArrowRight size={18} />
                    </button>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleReset}
                        className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                      >
                        Create Another
                      </button>
                    </div>
                    <NextStepGuide
                      currentStep="lead_magnet"
                      onPick={(stepKey) => { handleReset(); onClose(); onPickStep?.(stepKey); }}
                      onDone={handleClose}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
      
      {/* Trial Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowUpgradePrompt(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 max-w-md w-full border border-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock size={32} weight="fill" className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Upgrade to Continue</h3>
              <p className="text-slate-400 mb-6">{upgradeMessage}</p>
              <div className="space-y-3">
                <Link
                  to="/pricing"
                  className="block w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all"
                >
                  View Plans
                </Link>
                <button
                  onClick={() => setShowUpgradePrompt(false)}
                  className="w-full py-3 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
